import { BlogEntityDetails, BlogTemplateData } from '@/types'
import { BlogHeaderBg, ContentContainer, Wrapper } from '@/components/BlogPageTemplate.styles'
import { getBlogWidget, getBlogAttribute } from '@/helpers/WidgetHelper'
import Head from 'next/head'
import ContentBlock from '@/components/ContentBlock'
import ErrorPage from '@/components/ErrorPage'
import { createPageTitle, renderOpenGraph } from '@/helpers/PageTitleHelper'
import { getIndexTag } from '@/helpers/SeoHelper'
import { useDefaultProperties } from '@/hooks/DefaultPropertiesHook'
import { useSearchParams } from 'next/navigation'

type BlogPageTemplateProps = {
  contentPageDetails: BlogEntityDetails
  canonicalLink?: string
}

const BlogPageTemplate: React.FC<BlogPageTemplateProps> = ({ contentPageDetails, canonicalLink }) => {
  const { title, description, templateData, templateDataDraft, templateName, content, contentDraft } =
    contentPageDetails
  const defaultOpenGraphImage = 'https://cdn.experienceoz.com.au/assets/catalogueType/2/miscGraph/140716.jpg'
  const searchParams = useSearchParams()
  const isPreview = searchParams.get('preview')

  const defaultProperties = useDefaultProperties()
  const handlerKey = defaultProperties?.agentConfiguration?.handlerKey
  const isBrgAgent = defaultProperties?.agentConfiguration?.isBrgAgent

  const renderPreviewMeta = (templateParam: BlogTemplateData[]) => {
    const blogHeaderImage = templateParam[1].name === 'image'
    if (blogHeaderImage) {
      const headerImage = getBlogAttribute('attributes' in templateParam[1] ? templateParam[1].attributes : [], 'image')
      return renderOpenGraph(title, description, headerImage ?? defaultOpenGraphImage)
    } else {
      return renderOpenGraph(title, description, defaultOpenGraphImage)
    }
  }

  if (templateName === 'CUSTOM') {
    const renderTemplate = isPreview === 'true' ? templateDataDraft : templateData
    return (
      <Wrapper>
        <Head>
          <title>{createPageTitle(title)}</title>
          <meta name="description" property="og:description" content={description} />
          {getIndexTag(isBrgAgent, 'blog-page', handlerKey!)}
          {canonicalLink && <link rel="canonical" href={canonicalLink} />}
          {renderPreviewMeta(renderTemplate)}
        </Head>
        <BlogHeaderBg />
        <ContentContainer>
          {renderTemplate?.map((widget, index) => (
            <div key={index} style={{ width: '100%' }}>
              {getBlogWidget(widget, renderTemplate[index + 1])}
            </div>
          ))}
        </ContentContainer>
      </Wrapper>
    )
  }

  if (templateName === 'HTML') {
    const renderContent = isPreview === 'true' ? contentDraft : content
    return (
      <Wrapper>
        <Head>
          <title>{createPageTitle(title)}</title>
          <meta name="description" property="og:description" content={description} />
          <link rel="canonical" href={canonicalLink} />
          {renderOpenGraph(title, description, defaultOpenGraphImage)}
        </Head>
        <ContentBlock content={renderContent || ''} rawHTML />
      </Wrapper>
    )
  } else {
    return <ErrorPage />
  }
}

export default BlogPageTemplate
